import { HttpClient, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

const HUBSPOT_PORTAL_ID = '7071814'
const URL_BASE = 'https://api.hsforms.com/submissions/v3/integration/submit'

export interface HubspotFormResponse {
  inlineMessage: string
}

interface HubspotField {
  name: string
  value: string
}

interface HubspotFormRequest {
  hutk: string
  fields: HubspotField[]
}

export interface HubspotForm {
  id: string
  fieldMap: [string, string][]
}

const NEW_USER = {
  id: '79fc4382-ff32-40c0-b6fc-6f69f4c417ef',
  fieldMap: [
    ['email', 'email'],
    ['displayName', 'lastname'],
  ],
} as HubspotForm

const OPEN_TICKET = {
  id: '534ca947-84ea-4747-a1d7-f24e09a71daf',
  fieldMap: [
    ['email', 'email'],
    ['ticketSubject', 'TICKET.subject'],
    ['ticketPriority', 'TICKET.hs_ticket_priority'],
    ['ticketContent', 'TICKET.content'],
  ],
} as HubspotForm

export function formToFields(form: HubspotForm, data: object): HubspotField[] {
  return form.fieldMap.map(([a, b]) => ({ name: b, value: data[a] }) as HubspotField)
}

@Injectable({
  providedIn: 'root',
})
export class HubspotService {
  constructor(private http: HttpClient) {}

  private post$<T, RT = T>(
    url: string,
    data: T,
    params: { [param: string]: string } = null
  ): Observable<HttpResponse<RT>> {
    const options: {
      observe: 'response'
      withCredentials: boolean
      params?: {
        [param: string]: string
      }
    } = { observe: 'response', withCredentials: false }
    if (!!params) {
      options.params = params
    }
    return this.http.post<RT>(url, data, options)
  }

  private get hubspotUtk() {
    // parse the value of the hubspotutk cookie from the current cookies.
    return document.cookie
      .split('; ')
      .filter((kv) => kv.startsWith('hubspotutk='))
      .map((kv) => kv.split('=')[1])
      .pop()
  }

  private submitForm$(form: HubspotForm, data: object) {
    const url = [URL_BASE, HUBSPOT_PORTAL_ID, form.id].join('/')
    const request = { hutk: this.hubspotUtk, fields: formToFields(form, data) } as HubspotFormRequest
    return this.post$<HubspotFormRequest, HubspotFormResponse>(url, request)
  }

  public notifyOfNewUser$(email: string, displayName: string) {
    if (environment.registerSignupsInHubspot) {
      // post to a Hubspot form to capture the new user
      return this.submitForm$(NEW_USER, { email, displayName }).pipe(
        map((response) => response.body),
        // Swallow error, users don't want to know about Hubspot!
        catchError((error) => {
          console.error(error)
          return of(null)
        })
      )
    } else {
      return of(null)
    }
  }

  public openTicket$(data: object) {
    return this.submitForm$(OPEN_TICKET, data).pipe(map((response) => response.body))
  }
}
